// Joana chat shell — the embedded MINI-APP HOST (ADR-0011 build step 4). This is
// what makes the web client "Option A": untrusted third-party mini-apps run in the
// SAME page as the trusted chat shell, but FULLY SANDBOXED — their logic executes
// in the WASM isolate inside a Web Worker (off the DOM thread), they paint only
// through the Joana render layer into an isolated viewport, and they reach
// platform features ONLY through the capability broker. The shell owns the chrome
// (the close affordance); a mini-app can neither draw it nor escape the sandbox.
//
// This module is the DOM-agnostic SESSION manager: given a transport to a worker
// host, a renderer factory for the mini-app's viewport, and the trusted
// main-thread capability handlers, it launches/closes one mini-app. The browser
// glue (app/main.ts) wires it to a real Worker + a DOM renderer + the overlay;
// tests wire it to an in-process worker host + a RecordingRenderer.

import { JoanaClient, type AuditEvent, type MainCapabilityHandler, type RendererFactory } from "../worker/client.ts";
import type { Transport } from "../worker/protocol.ts";
import { parseManifest } from "../runtime/manifest.ts";
import type { MiniAppPackage } from "./types.ts";

export type MiniAppStatus = "idle" | "loading" | "running" | "error";

/** A live worker session: the main-thread transport + a teardown for the worker
 *  itself (terminate the Worker / drop the in-process host). */
export interface WorkerSession {
  transport: Transport;
  dispose: () => void;
}

export interface MiniAppHostOptions {
  /** Spawn a fresh worker session (a Worker in the browser; an in-process host in
   *  tests). A NEW session per open keeps mini-apps isolated from each other. */
  createSession: () => WorkerSession;
  /** Builds the renderer for the mini-app's sandboxed viewport. */
  contentRenderer: RendererFactory;
  /** Trusted main-thread capability handlers (share.sheet, notifications.local,
   *  payments.intent, …). The worker proxies UI-bearing capabilities here. */
  capabilities?: Record<string, MainCapabilityHandler>;
  onAudit?: (e: AuditEvent) => void;
  onStatus?: (status: MiniAppStatus, error: string | null) => void;
}

export class MiniAppHost {
  private client: JoanaClient | null = null;
  private session: WorkerSession | null = null;
  private currentAppId: string | null = null;
  private status: MiniAppStatus = "idle";

  private readonly opts: MiniAppHostOptions;

  constructor(opts: MiniAppHostOptions) {
    this.opts = opts;
  }

  get isOpen(): boolean {
    return this.client !== null;
  }

  get appId(): string | null {
    return this.currentAppId;
  }

  /**
   * Launch a mini-app package (validated manifest + bundle). Tears down any
   * currently running app first (one mini-app at a time, like a WeChat page).
   * Resolves once onLaunch has run (status "running"); on any failure the status
   * is "error" and the session is torn down.
   */
  async open(pkg: MiniAppPackage): Promise<void> {
    this.close(); // one at a time

    // Re-validate the manifest on the client even though publish-time validated
    // it — defense in depth (rejects unknown capabilities before we launch).
    const manifest = parseManifest(pkg.manifest);
    this.currentAppId = manifest.app_id;
    this.setStatus("loading", null);

    const session = this.opts.createSession();
    this.session = session;

    const client = new JoanaClient(session.transport, { renderer: this.opts.contentRenderer });
    this.client = client;
    if (this.opts.onAudit) client.onAudit = this.opts.onAudit;
    for (const [grant, handler] of Object.entries(this.opts.capabilities ?? {})) {
      client.registerCapability(grant, handler);
    }

    try {
      const launched = await client.launch(manifest, pkg.code);
      if (launched.status !== 0) {
        throw new Error(launched.error || "mini-app failed to evaluate");
      }
      await client.callHandler("onLaunch");
      // Guard against a close() racing the async launch.
      if (this.client === client) this.setStatus("running", null);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (this.client === client) {
        this.close();
        this.setStatus("error", message);
      }
      throw e;
    }
  }

  /** Tear down the running mini-app: dispose the isolate + terminate the worker. */
  close(): void {
    if (this.client) {
      this.client.dispose();
      this.client = null;
    }
    if (this.session) {
      this.session.dispose();
      this.session = null;
    }
    this.currentAppId = null;
    if (this.status !== "error") this.setStatus("idle", null);
  }

  private setStatus(status: MiniAppStatus, error: string | null): void {
    this.status = status;
    this.opts.onStatus?.(status, error);
  }
}
